import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import TrialBadge from '../components/TrialBadge';
import TrialConversionModal from '../components/TrialConversionModal';
import { 
  Film, 
  Plus, 
  Clock, 
  MoreVertical, 
  Trash2, 
  Edit3, 
  Users,
  LogOut,
  Settings,
  Crown,
  Folder,
  FileText,
  X,
  Play,
  Sparkles,
  Trophy,
  ArrowRight,
  CheckCircle,
  ExternalLink,
  Square,
  CheckSquare,
  Instagram,
  ShoppingBag
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Competition deadline - Fixed date: May 21, 2026 at midnight UTC
const COMPETITION_DEADLINE = new Date('2026-05-21T00:00:00Z');

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showPlotModal, setShowPlotModal] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [selectedChallengeProject, setSelectedChallengeProject] = useState(null);
  const [challengeEntries, setChallengeEntries] = useState([]);
  const [confirmations, setConfirmations] = useState({
    storyFoundation: false,
    followSocial: false,
    shareChallenge: false,
    originalWork: false
  });
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  // Trial and script pass state
  const [trialStatus, setTrialStatus] = useState(null);
  const [scriptPasses, setScriptPasses] = useState(null);
  const [showTrialEndedModal, setShowTrialEndedModal] = useState(false);

  // Fetch trial status and script passes
  useEffect(() => {
    const fetchTrialAndPasses = async () => {
      const token = localStorage.getItem('moviebot_token');
      if (!token) return;

      try {
        const [trialRes, passesRes] = await Promise.all([
          fetch(`${API_URL}/api/user/trial-status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/user/script-passes`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (trialRes.ok) {
          const data = await trialRes.json();
          setTrialStatus(data);
          
          // Show trial ended modal if trial just expired and user hasn't subscribed
          if (data.has_trial && !data.trial_active && data.subscription_tier === 'free') {
            // Check if we've shown this modal before (use localStorage)
            const modalShown = localStorage.getItem('trial_ended_modal_shown');
            if (!modalShown) {
              setShowTrialEndedModal(true);
              localStorage.setItem('trial_ended_modal_shown', 'true');
            }
          }
        }

        if (passesRes.ok) {
          const data = await passesRes.json();
          setScriptPasses(data);
        }
      } catch (error) {
        console.error('Error fetching trial/passes:', error);
      }
    };

    fetchTrialAndPasses();
  }, []);

  // Countdown timer
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = COMPETITION_DEADLINE - now;
      
      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
      }
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('moviebot_token');
      const response = await fetch(`${API_URL}/api/projects`, {
        
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
      
      // Also fetch challenge entries to know which projects are entered
      const entriesRes = await fetch(`${API_URL}/api/challenge/my-entries`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (entriesRes.ok) {
        const entries = await entriesRes.json();
        setChallengeEntries(entries);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      const token = localStorage.getItem('moviebot_token');
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        setProjects(prev => prev.filter(p => p.project_id !== projectId));
        toast.success('Project deleted');
      }
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const genreColors = {
    drama: 'bg-blue-500/20 text-blue-400',
    comedy: 'bg-yellow-500/20 text-yellow-400',
    thriller: 'bg-red-500/20 text-red-400',
    scifi: 'bg-purple-500/20 text-purple-400',
    horror: 'bg-red-700/20 text-red-300',
    romance: 'bg-pink-500/20 text-pink-400',
    action: 'bg-orange-500/20 text-orange-400',
    documentary: 'bg-green-500/20 text-green-400'
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-secondary/95 backdrop-blur-sm border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Film className="w-6 h-6 text-primary" />
          <span className="font-black text-lg tracking-tight text-white">MOVIEBOT</span>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-white hover:bg-white/10 rounded"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-64 bg-secondary/30 border-r border-white/5 p-6 flex flex-col z-50 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 mb-10 hidden lg:flex" data-testid="dashboard-logo">
          <Film className="w-8 h-8 text-primary" />
          <span className="font-black text-xl tracking-tight text-white">MOVIEBOT</span>
        </Link>

        {/* User Info */}
        <div className="mb-8 p-4 bg-black/30 border border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="text-primary font-bold">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          
          {/* Subscription/Trial Badge */}
          <div className="mt-3">
            {user?.subscription_tier === 'pro' && (
              <div className="flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/30 px-3 py-1.5 rounded-full">
                <Crown className="w-4 h-4 text-yellow-500" />
                <span className="text-yellow-500 text-xs font-bold">PRO</span>
              </div>
            )}
            {user?.subscription_tier === 'basic' && (
              <div className="flex items-center gap-2 bg-primary/20 border border-primary/30 px-3 py-1.5 rounded-full">
                <Crown className="w-4 h-4 text-primary" />
                <span className="text-primary text-xs font-bold">BASIC</span>
              </div>
            )}
            {trialStatus?.trial_active && user?.subscription_tier === 'free' && (
              <Link to="/subscription">
                <div className="flex items-center gap-2 bg-purple-500/20 border border-purple-500/30 px-3 py-1.5 rounded-full hover:bg-purple-500/30 transition-colors">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span className="text-purple-400 text-xs font-bold">PRO TRIAL</span>
                  <span className="text-purple-300 text-xs">{trialStatus.days_left}d</span>
                </div>
              </Link>
            )}
            {!trialStatus?.trial_active && user?.subscription_tier === 'free' && (
              <Link to="/subscription">
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full hover:bg-white/10 transition-colors">
                  <Crown className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground text-xs">Upgrade</span>
                </div>
              </Link>
            )}
          </div>

          {/* Script Passes */}
          {((scriptPasses?.script_passes || 0) + (scriptPasses?.script_passes_pro || 0) > 0) && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <ShoppingBag className="w-3 h-3 text-emerald-400" />
              <span className="text-emerald-400">
                {scriptPasses?.script_passes || 0} Pass{(scriptPasses?.script_passes || 0) !== 1 ? 'es' : ''}
              </span>
              {(scriptPasses?.script_passes_pro || 0) > 0 && (
                <span className="text-yellow-400">
                  + {scriptPasses.script_passes_pro} Pro
                </span>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="space-y-2 flex-1">
          <Link to="/dashboard" onClick={() => setSidebarOpen(false)}>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-white hover:bg-white/10"
              data-testid="nav-projects"
            >
              <Folder className="w-4 h-4 mr-3" />
              My Projects
            </Button>
          </Link>
          <Link to="/challenge" onClick={() => setSidebarOpen(false)}>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-teal-400 hover:text-teal-300 hover:bg-teal-500/10"
              data-testid="nav-challenge"
            >
              <Trophy className="w-4 h-4 mr-3" />
              Summer Competition
            </Button>
          </Link>
          <Link to="/subscription" onClick={() => setSidebarOpen(false)}>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-muted-foreground hover:text-white hover:bg-white/10"
              data-testid="nav-subscription"
            >
              <Crown className="w-4 h-4 mr-3" />
              Subscription
            </Button>
          </Link>
          <Link to="/settings" onClick={() => setSidebarOpen(false)}>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-muted-foreground hover:text-white hover:bg-white/10"
              data-testid="nav-settings"
            >
              <Settings className="w-4 h-4 mr-3" />
              Settings
            </Button>
          </Link>
        </nav>

        {/* Social Media Links */}
        <div className="border-t border-white/10 pt-4 mb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3 px-3">Follow Us</p>
          <div className="flex items-center justify-center gap-3">
            <a 
              href="https://instagram.com/moviebot.tech" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-pink-500/20 flex items-center justify-center text-muted-foreground hover:text-pink-400 transition-all duration-200"
              data-testid="social-instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a 
              href="https://tiktok.com/@moviebot.tech" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/20 flex items-center justify-center text-muted-foreground hover:text-white transition-all duration-200"
              data-testid="social-tiktok"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Logout */}
        <Button 
          variant="ghost" 
          onClick={() => {
            handleLogout();
            setSidebarOpen(false);
          }}
          className="w-full justify-start text-muted-foreground hover:text-white hover:bg-white/10"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-3" />
          Sign Out
        </Button>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-white">My Projects</h1>
            <p className="text-muted-foreground text-sm lg:text-base mt-1">Manage your screenplays</p>
          </div>
          <Link to="/new-project">
            <Button 
              className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-xs px-6 py-4 btn-glow"
              data-testid="new-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </Link>
        </div>

        {/* Competition Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 bg-gradient-to-r from-teal-900/40 via-teal-800/30 to-emerald-900/40 border border-teal-500/30 p-5 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          
          <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                <Trophy className="w-6 h-6 text-teal-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1">
                  Summer Film Competition
                </h3>
                <p className="text-sm text-muted-foreground">
                  Submit your screenplay. We produce the winner and submit to streaming.
                </p>
                <div className="flex items-center gap-3 mt-2 text-xs text-teal-400 font-mono">
                  <Sparkles className="w-3 h-3" />
                  <span>Open Now — Summer 2026</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 w-full lg:w-auto">
              <Link to="/challenge" className="flex-1 lg:flex-none">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="w-full border-teal-500/30 text-teal-400 hover:bg-teal-500/10 hover:text-teal-300"
                >
                  <ExternalLink className="w-3 h-3 mr-2" />
                  View Rules
                </Button>
              </Link>
              <Button 
                size="sm"
                className="flex-1 lg:flex-none bg-teal-500 text-white hover:bg-teal-400 font-bold"
                onClick={() => setShowChallengeModal(true)}
                data-testid="join-challenge-btn"
              >
                <Trophy className="w-3 h-3 mr-2" />
                Enter Competition
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Projects Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-64 bg-secondary/30 animate-pulse" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <Film className="w-16 h-16 text-muted-foreground/50 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-white mb-2">No projects yet</h2>
            <p className="text-muted-foreground mb-6">Create your first screenplay to get started</p>
            <Link to="/new-project">
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-xs px-8 py-4"
                data-testid="empty-new-project-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {projects.map((project, index) => (
              <motion.div
                key={project.project_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group relative bg-secondary/30 border border-white/5 hover:border-primary/50 transition-all duration-300"
                data-testid={`project-card-${project.project_id}`}
              >
                {/* Project Header */}
                <div className="p-6 relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <span className={`text-xs font-bold uppercase px-3 py-1 ${genreColors[project.genre] || 'bg-white/10 text-white'}`}>
                      {project.genre}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-muted-foreground hover:text-white relative z-20"
                          data-testid={`project-menu-${project.project_id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-secondary border-white/10 z-50">
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/editor/${project.project_id}`);
                          }}
                          className="text-white hover:bg-white/10 cursor-pointer"
                        >
                          <Edit3 className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/10" />
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(project.project_id);
                          }}
                          className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">{project.title}</h3>
                  {project.logline && (
                    <p className="text-muted-foreground text-sm line-clamp-2 mb-4">{project.logline}</p>
                  )}
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Act {project.current_act}/3
                    </span>
                    {project.collaborators?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {project.collaborators.length}
                      </span>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 relative z-10">
                    <Button
                      size="sm"
                      className="w-full bg-primary text-black hover:bg-primary/90 text-xs"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/editor/${project.project_id}`);
                      }}
                      data-testid={`work-on-${project.project_id}`}
                    >
                      <Play className="w-3 h-3 mr-1" />
                      Work on Script
                    </Button>
                    {(project.logline || project.plot_outline || (project.characters && project.characters.length > 0)) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-white/20 text-white hover:bg-white/10 text-xs"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedProject(project);
                          setShowPlotModal(true);
                        }}
                        data-testid={`view-story-${project.project_id}`}
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Story Foundation
                      </Button>
                    )}
                  </div>
                </div>

                {/* Click overlay removed - using buttons instead */}
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Plot Outline Modal */}
      <AnimatePresence>
        {showPlotModal && selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPlotModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-secondary border border-white/10 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-white/10 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 ${genreColors[selectedProject.genre] || 'bg-white/10 text-white'}`}>
                      {selectedProject.genre}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-white">{selectedProject.title}</h2>
                  {selectedProject.logline && (
                    <p className="text-muted-foreground text-sm mt-2">{selectedProject.logline}</p>
                  )}
                </div>
                <button
                  onClick={() => setShowPlotModal(false)}
                  className="text-muted-foreground hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content - Story Foundation */}
              <div className="p-6 overflow-y-auto flex-1 space-y-6">
                {/* Logline Section */}
                {selectedProject.logline && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-bold text-white">Logline</h3>
                    </div>
                    <div className="bg-black/30 border border-white/10 p-4 rounded-lg">
                      <p className="text-white leading-relaxed italic">"{selectedProject.logline}"</p>
                    </div>
                  </div>
                )}

                {/* Plot Outline Section - Handles both string and object formats */}
                {selectedProject.plot_outline && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-green-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white">Plot Outline</h3>
                    </div>
                    <div className="space-y-4">
                      {/* Handle object format with act_1_setup, act_2_confrontation, act_3_resolution */}
                      {typeof selectedProject.plot_outline === 'object' && selectedProject.plot_outline !== null ? (
                        <>
                          {selectedProject.plot_outline.act_1_setup && (
                            <div className="border-l-4 border-green-500/50 bg-green-500/5 p-4 rounded-r-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-bold uppercase px-2 py-1 rounded bg-green-500/20 text-green-400">
                                  Act 1
                                </span>
                                <span className="text-sm font-medium text-white">Setup</span>
                              </div>
                              <p className="text-sm text-muted-foreground leading-relaxed">{selectedProject.plot_outline.act_1_setup}</p>
                            </div>
                          )}
                          {selectedProject.plot_outline.act_2_confrontation && (
                            <div className="border-l-4 border-yellow-500/50 bg-yellow-500/5 p-4 rounded-r-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-bold uppercase px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">
                                  Act 2
                                </span>
                                <span className="text-sm font-medium text-white">Confrontation</span>
                              </div>
                              <p className="text-sm text-muted-foreground leading-relaxed">{selectedProject.plot_outline.act_2_confrontation}</p>
                            </div>
                          )}
                          {selectedProject.plot_outline.act_3_resolution && (
                            <div className="border-l-4 border-red-500/50 bg-red-500/5 p-4 rounded-r-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-bold uppercase px-2 py-1 rounded bg-red-500/20 text-red-400">
                                  Act 3
                                </span>
                                <span className="text-sm font-medium text-white">Resolution</span>
                              </div>
                              <p className="text-sm text-muted-foreground leading-relaxed">{selectedProject.plot_outline.act_3_resolution}</p>
                            </div>
                          )}
                        </>
                      ) : typeof selectedProject.plot_outline === 'string' ? (
                        /* Handle string format - parse by ACT markers */
                        (() => {
                          const plotText = selectedProject.plot_outline;
                          const actParts = plotText.split(/(?=ACT\s*[1-3])/i).filter(p => p.trim());
                          
                          if (actParts.length > 1) {
                            return actParts.map((actContent, idx) => {
                              const headerMatch = actContent.match(/^ACT\s*([1-3])\s*[-–:]*\s*([A-Z]+)?/i);
                              const actNum = headerMatch ? headerMatch[1] : String(idx + 1);
                              const extractedName = headerMatch && headerMatch[2] ? headerMatch[2] : null;
                              const actName = extractedName || 
                                (actNum === '1' ? 'Setup' : actNum === '2' ? 'Confrontation' : 'Resolution');
                              
                              const actColor = actNum === '1' ? 'border-green-500/50 bg-green-500/5' : 
                                actNum === '2' ? 'border-yellow-500/50 bg-yellow-500/5' : 'border-red-500/50 bg-red-500/5';
                              const labelColor = actNum === '1' ? 'bg-green-500/20 text-green-400' : 
                                actNum === '2' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400';
                              
                              const cleanContent = actContent.replace(/^ACT\s*[1-3]\s*[-–:]*\s*[A-Z]*\s*[-–:]*\s*/i, '').trim();
                              
                              return (
                                <div key={idx} className={`border-l-4 ${actColor} p-4 rounded-r-lg`}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${labelColor}`}>
                                      Act {actNum}
                                    </span>
                                    <span className="text-sm font-medium text-white capitalize">{actName.toLowerCase()}</span>
                                  </div>
                                  <p className="text-sm text-muted-foreground leading-relaxed">{cleanContent}</p>
                                </div>
                              );
                            });
                          } else {
                            return (
                              <div className="bg-black/30 border border-white/10 p-4 rounded-lg whitespace-pre-wrap text-sm text-muted-foreground leading-relaxed">
                                {plotText}
                              </div>
                            );
                          }
                        })()
                      ) : null}
                    </div>
                  </div>
                )}

                {/* Characters Section */}
                {selectedProject.characters && selectedProject.characters.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-purple-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white">Characters</h3>
                    </div>
                    <div className="grid gap-3">
                      {selectedProject.characters.map((char, idx) => (
                        <div key={idx} className="bg-black/30 border border-white/10 p-4 rounded-lg">
                          <p className="font-bold text-white text-base">{char.name}</p>
                          {char.role && (
                            <span className="text-xs text-primary uppercase tracking-wide">{char.role}</span>
                          )}
                          {char.description && (
                            <p className="text-sm text-muted-foreground mt-2">{char.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {!selectedProject.logline && !selectedProject.plot_outline && (!selectedProject.characters || selectedProject.characters.length === 0) && (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">No story foundation data available for this project.</p>
                  </div>
                )}

                {/* Challenge Opt-In Box - only show if not already entered */}
                {selectedProject.plot_outline && !challengeEntries.some(e => e.project_id === selectedProject.project_id) && (
                  <div className="mt-6 p-5 bg-gradient-to-r from-teal-500/20 to-teal-600/10 border border-teal-500/30 rounded-lg">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-teal-500/30 flex items-center justify-center flex-shrink-0">
                        <Trophy className="w-6 h-6 text-teal-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-bold text-lg mb-1">
                          This is a compelling story!
                        </h4>
                        <p className="text-teal-200/80 text-sm mb-4">
                          Your storyline has the foundation for something great. Submit it to the <strong>Summer Film Competition</strong> — we produce the winner!
                        </p>
                        <div className="flex items-center gap-3">
                          <Button
                            onClick={() => {
                              setShowPlotModal(false);
                              setSelectedChallengeProject(selectedProject);
                              setShowChallengeModal(true);
                            }}
                            className="bg-teal-500 hover:bg-teal-600 text-white font-bold"
                            data-testid="modal-challenge-btn"
                          >
                            <Trophy className="w-4 h-4 mr-2" />
                            Enter Competition
                          </Button>
                          <span className="text-xs text-teal-300/60 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            Summer 2026
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Challenge Already Entered Badge */}
                {selectedProject.plot_outline && challengeEntries.some(e => e.project_id === selectedProject.project_id) && (
                  <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <div>
                        <span className="text-green-400 font-bold">Challenge Entry Submitted!</span>
                        <p className="text-green-200/70 text-sm">Good luck! Winners will be announced after the deadline.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-white/10 bg-black/20">
                <Button
                  className="w-full bg-primary text-black hover:bg-primary/90 font-bold"
                  onClick={() => {
                    setShowPlotModal(false);
                    navigate(`/editor/${selectedProject.project_id}`);
                  }}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Continue Writing This Script
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Challenge Entry Modal */}
      <AnimatePresence>
        {showChallengeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowChallengeModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-secondary border border-teal-500/30 max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-white/10 bg-gradient-to-r from-teal-900/30 to-emerald-900/30">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-teal-500/20 flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-teal-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Summer Film Competition</h2>
                      <p className="text-sm text-teal-400 font-mono mt-1">
                        Open Now — Summer 2026
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowChallengeModal(false)}
                    className="text-muted-foreground hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto flex-1 space-y-6">
                {/* Select Project */}
                <div>
                  <label className="text-sm font-medium text-white mb-3 block">
                    Select Your Screenplay
                  </label>
                  {projects.length === 0 ? (
                    <div className="bg-black/30 border border-white/10 p-4 rounded-lg text-center">
                      <p className="text-muted-foreground text-sm mb-3">
                        You need a project to enter the challenge
                      </p>
                      <Link to="/new-project">
                        <Button size="sm" className="bg-primary text-black">
                          <Plus className="w-3 h-3 mr-1" />
                          Create Project
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {projects.map((project) => (
                        <button
                          key={project.project_id}
                          onClick={() => setSelectedChallengeProject(project)}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            selectedChallengeProject?.project_id === project.project_id
                              ? 'bg-teal-500/20 border-teal-500/50'
                              : 'bg-black/30 border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-white text-sm">{project.title}</p>
                              <p className="text-xs text-muted-foreground capitalize">{project.genre}</p>
                            </div>
                            {selectedChallengeProject?.project_id === project.project_id && (
                              <CheckCircle className="w-5 h-5 text-teal-400" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Confirmation Checkboxes */}
                {selectedChallengeProject && (
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-white mb-3 block">
                      Confirm the following:
                    </label>
                    
                    {[
                      { key: 'storyFoundation', label: 'My story foundation is complete (logline, outline, characters)' },
                      { key: 'followSocial', label: 'I follow @moviebot.tech on TikTok and Instagram' },
                      { key: 'shareChallenge', label: 'I will share this competition with #MovieBotSummer' },
                      { key: 'originalWork', label: 'This is my original, unpublished work' }
                    ].map((item) => (
                      <button
                        key={item.key}
                        onClick={() => setConfirmations(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                        className="w-full flex items-start gap-3 text-left p-3 rounded-lg bg-black/20 border border-white/10 hover:border-white/20 transition-colors"
                      >
                        {confirmations[item.key] ? (
                          <CheckSquare className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Square className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        )}
                        <span className={`text-sm ${confirmations[item.key] ? 'text-white' : 'text-muted-foreground'}`}>
                          {item.label}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Requirements Note */}
                <div className="bg-black/30 border border-white/10 p-4 rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    <span className="text-white font-medium">Requirements:</span> Complete screenplay (70+ pages for features, 10+ pages for shorts). 
                    Active MovieBot Basic or Pro subscription required.{' '}
                    <Link to="/challenge" className="text-teal-400 hover:underline">
                      View full rules
                    </Link>
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-white/10 bg-black/20">
                <Button
                  className="w-full bg-teal-500 text-white hover:bg-teal-400 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={
                    !selectedChallengeProject || 
                    !confirmations.storyFoundation || 
                    !confirmations.followSocial || 
                    !confirmations.shareChallenge || 
                    !confirmations.originalWork
                  }
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('moviebot_token');
                      const response = await fetch(`${API_URL}/api/challenge/enter`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                          project_id: selectedChallengeProject.project_id,
                          confirmations: confirmations
                        })
                      });
                      
                      if (response.ok) {
                        toast.success('Competition entry confirmed! Complete your screenplay and submit before the deadline.');
                        setShowChallengeModal(false);
                        navigate(`/editor/${selectedChallengeProject.project_id}`);
                      } else {
                        const data = await response.json();
                        toast.error(data.detail || 'Failed to enter competition');
                      }
                    } catch (error) {
                      toast.error('Failed to enter competition');
                    }
                  }}
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Confirm Entry & Continue Writing
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-3">
                  You can update your screenplay anytime before the deadline
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trial Ended Conversion Modal */}
      <TrialConversionModal
        isOpen={showTrialEndedModal}
        onClose={() => setShowTrialEndedModal(false)}
        trialUsage={trialStatus?.trial_usage}
      />
    </div>
  );
};

export default Dashboard;
